import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Compare feature - keeps up to 3 home-design plans in a "compare"
 * bucket that persists across pages and reloads (localStorage).
 *
 * Distinct from Favourites (which is the long-lived "My shortlist"
 * concept). Compare is intentionally lightweight: max 3, meant to be
 * cleared often, and its main job is to feed a richer 3-plan brief
 * into the matcher.
 *
 * A plan is stored as a minimal snapshot so /home-designs/compare
 * can render side-by-side without re-fetching. If a plan is stale
 * (renamed, delisted) the compare page will fall back to the snapshot.
 */

const STORAGE_KEY = 'bp_compare_plans_v1';
const MAX_PLANS = 3;

const CompareContext = createContext(null);

const pickSnapshot = (plan) => {
  if (!plan) return null;
  return {
    full_slug: plan.full_slug,
    builder_slug: plan.builder_slug,
    plan_slug: plan.plan_slug,
    plan_name: plan.plan_name,
    builder_name: plan.builder_name,
    builder_logo: plan.builder_logo,
    facade: plan.facade,
    bedrooms: plan.bedrooms,
    bathrooms: plan.bathrooms,
    garage: plan.garage,
    stories: plan.stories,
    total_sqm: plan.total_sqm,
    house_width: plan.house_width,
    house_depth: plan.house_depth,
    project_type: plan.project_type,
  };
};

const loadInitial = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, MAX_PLANS) : [];
  } catch {
    return [];
  }
};

export const CompareProvider = ({ children }) => {
  const [comparePlans, setComparePlans] = useState(loadInitial);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(comparePlans));
    } catch { /* private-mode / quota-exceeded - non-fatal */ }
  }, [comparePlans]);

  const isInCompare = useCallback(
    (fullSlug) => comparePlans.some((p) => p.full_slug === fullSlug),
    [comparePlans],
  );

  const addToCompare = useCallback((plan) => {
    const snap = pickSnapshot(plan);
    if (!snap?.full_slug) return { ok: false, reason: 'invalid' };
    if (comparePlans.some((p) => p.full_slug === snap.full_slug)) {
      return { ok: false, reason: 'already-added' };
    }
    if (comparePlans.length >= MAX_PLANS) {
      setToastMessage('You can compare up to 3 plans. Remove one first.');
      setTimeout(() => setToastMessage(''), 3200);
      return { ok: false, reason: 'full' };
    }
    setComparePlans((prev) => [...prev, snap]);
    setToastMessage(`Added ${snap.plan_name} to compare`);
    setTimeout(() => setToastMessage(''), 2400);
    return { ok: true };
  }, [comparePlans]);

  const removeFromCompare = useCallback((fullSlug) => {
    setComparePlans((prev) => prev.filter((p) => p.full_slug !== fullSlug));
  }, []);

  const toggleCompare = useCallback((plan) => {
    const snap = pickSnapshot(plan);
    if (!snap?.full_slug) return { ok: false };
    if (comparePlans.some((p) => p.full_slug === snap.full_slug)) {
      removeFromCompare(snap.full_slug);
      return { ok: true, removed: true };
    }
    return addToCompare(plan);
  }, [comparePlans, addToCompare, removeFromCompare]);

  const clearCompare = useCallback(() => {
    setComparePlans([]);
  }, []);

  /**
   * Replace the entire compare bucket with a supplied list. Used by
   * shareable URL hydration (?p=slug1,slug2,slug3) - the compare page
   * fetches each slug's metadata then hands the snapshots here so the
   * receiver sees the exact same shortlist as the sharer.
   */
  const setComparePlansBulk = useCallback((plans) => {
    if (!Array.isArray(plans)) return;
    const snaps = plans.map(pickSnapshot).filter(Boolean).slice(0, MAX_PLANS);
    setComparePlans(snaps);
  }, []);

  const value = useMemo(() => ({
    comparePlans,
    isInCompare,
    addToCompare,
    removeFromCompare,
    toggleCompare,
    clearCompare,
    setComparePlansBulk,
    toastMessage,
    maxPlans: MAX_PLANS,
  }), [comparePlans, isInCompare, addToCompare, removeFromCompare, toggleCompare, clearCompare, setComparePlansBulk, toastMessage]);

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) {
    // Defensive: allow components to be rendered outside the provider
    // in isolation without exploding. In practice App.js wraps
    // everything in CompareProvider so this branch should not hit.
    return {
      comparePlans: [],
      isInCompare: () => false,
      addToCompare: () => ({ ok: false }),
      removeFromCompare: () => {},
      toggleCompare: () => ({ ok: false }),
      clearCompare: () => {},
      setComparePlansBulk: () => {},
      toastMessage: '',
      maxPlans: MAX_PLANS,
    };
  }
  return ctx;
};

export default CompareContext;
